import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Edit, Trash2 } from 'lucide-react';

const defaultForm = { name: '', product_id: '', total_price: 0, booking_fee: 0, dp_amount: 0, dp_percentage: 0, num_installments: 1, installment_type: 'fixed', grace_period_days: 3, late_fee: 0, plan_type: 'dp_installment', is_active: true };

export default function TenantPaymentPlans() {
  const [plans, setPlans] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(defaultForm);

  const fetch = async () => { const r = await api.get('/tenant/payment-plans'); setPlans(r.data.plans); };
  useEffect(() => { fetch(); }, []);

  const handleSave = async () => {
    if (editing) await api.put(`/tenant/payment-plans/${editing}`, form);
    else await api.post('/tenant/payment-plans', form);
    setShowDialog(false); setEditing(null); setForm(defaultForm); fetch();
  };
  const handleDelete = async (id) => { if (window.confirm('Hapus?')) { await api.delete(`/tenant/payment-plans/${id}`); fetch(); } };
  const edit = (p) => { setEditing(p.id); setForm(p); setShowDialog(true); };
  const fmt = (v) => new Intl.NumberFormat('id-ID').format(v || 0);

  return (
    <div data-testid="tenant-payment-plans-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Plus Jakarta Sans' }}>Payment Plan</h1>
        <Button onClick={() => { setEditing(null); setForm(defaultForm); setShowDialog(true); }} className="bg-[#2D6CDF] hover:bg-[#1A5BC4]" data-testid="add-plan-btn"><Plus size={16} className="mr-2" /> Tambah Plan</Button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {plans.map(p => (
          <Card key={p.id} className="border-slate-200 shadow-sm">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-slate-900">{p.name}</h3>
                <div className="flex gap-1">
                  <Button variant="ghost" size="icon" onClick={() => edit(p)}><Edit size={14} /></Button>
                  <Button variant="ghost" size="icon" onClick={() => handleDelete(p.id)}><Trash2 size={14} className="text-red-500" /></Button>
                </div>
              </div>
              <div className="space-y-1 text-sm text-slate-600">
                <p>Total: <span className="font-medium text-slate-900">Rp {fmt(p.total_price)}</span></p>
                <p>Tipe: <span className="capitalize">{p.plan_type}</span></p>
                <p>DP: Rp {fmt(p.dp_amount)} {p.dp_percentage > 0 && `(${p.dp_percentage}%)`}</p>
                <p>Booking Fee: Rp {fmt(p.booking_fee)}</p>
                <p>Termin: {p.num_installments}x</p>
                <p>Grace Period: {p.grace_period_days} hari</p>
              </div>
              <span className={`mt-3 inline-block px-2 py-0.5 rounded-full text-xs font-medium ${p.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>{p.is_active ? 'Aktif' : 'Nonaktif'}</span>
            </CardContent>
          </Card>
        ))}
        {!plans.length && <div className="col-span-full py-12 text-center text-slate-400">Belum ada payment plan</div>}
      </div>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? 'Edit Plan' : 'Tambah Plan'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Nama Plan</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="plan-name-input" /></div>
            <div><Label>Tipe Plan</Label>
              <Select value={form.plan_type} onValueChange={(v) => setForm({ ...form, plan_type: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="full_payment">Full Payment</SelectItem>
                  <SelectItem value="booking_full">Booking + Full</SelectItem>
                  <SelectItem value="dp_full">DP + Pelunasan</SelectItem>
                  <SelectItem value="dp_installment">DP + Termin</SelectItem>
                  <SelectItem value="monthly_installment">Cicilan Bulanan</SelectItem>
                  <SelectItem value="milestone">Milestone</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Total Harga (Rp)</Label><Input type="number" value={form.total_price} onChange={(e) => setForm({ ...form, total_price: parseInt(e.target.value) || 0 })} /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Booking Fee</Label><Input type="number" value={form.booking_fee} onChange={(e) => setForm({ ...form, booking_fee: parseInt(e.target.value) || 0 })} /></div>
              <div><Label>DP Amount</Label><Input type="number" value={form.dp_amount} onChange={(e) => setForm({ ...form, dp_amount: parseInt(e.target.value) || 0 })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Jumlah Termin</Label><Input type="number" value={form.num_installments} onChange={(e) => setForm({ ...form, num_installments: parseInt(e.target.value) || 1 })} /></div>
              <div><Label>Grace Period (hari)</Label><Input type="number" value={form.grace_period_days} onChange={(e) => setForm({ ...form, grace_period_days: parseInt(e.target.value) || 0 })} /></div>
            </div>
            <Button onClick={handleSave} className="w-full bg-[#2D6CDF] hover:bg-[#1A5BC4]" data-testid="save-plan-btn">Simpan</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
